import { css, keyframes } from '@emotion/css';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Content, BackDrop } from '@volumental/common';
import React from 'react';
import { uiConfig } from '../../services/config';
import { Header } from '../accessible-components';

const slideIn = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0px);
  }
`;

const container = css`
  position: fixed;
  inset: 0;
  z-index: 2;
`;

const modal = (fitContent: boolean, shouldHaveHeader: boolean): string => css`
  font-family: ${uiConfig.styles.fontFamily};
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: white;
  border-radius: 25px 25px 0 0;
  display: flex;
  flex-direction: column;
  ${fitContent
    ? `max-height: calc(100% - 34px);`
    : `height: calc(100% - 34px);`}
  animation: ${slideIn} 0.3s ease forwards;
  z-index: 3;
  overflow: hidden;
  outline: none;
  ${!shouldHaveHeader && `padding-top: 24px;`}
`;

export interface ModalProps {
  show: boolean;
  fitContent?: boolean;
  close?: () => void;
  back?: () => void;
  headerTitle?: string;
  disableGutters?: boolean;
  disableScrollBar?: boolean;
  modalTitle: string;
}

export function Modal({
  show,
  fitContent = false,
  close,
  back,
  headerTitle,
  modalTitle,
  disableGutters = false,
  disableScrollBar = true,
  children,
}: React.PropsWithChildren<ModalProps>): React.JSX.Element | null {
  const shouldHaveHeader =
    back !== undefined || close !== undefined || headerTitle !== undefined;
  return (
    <Dialog.Root open={show} onOpenChange={(open) => !open && close?.()}>
      <Dialog.Portal>
        <div className={container}>
          <BackDrop visible={show} animationTime="0.3s" onClick={close} />
          <Dialog.Content
            aria-describedby={undefined}
            className={modal(fitContent, shouldHaveHeader)}
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              close?.();
            }}
          >
            <Dialog.Title asChild>
              <VisuallyHidden>{modalTitle}</VisuallyHidden>
            </Dialog.Title>
            {shouldHaveHeader && (
              <Header close={close} back={back} title={headerTitle} />
            )}
            <Content
              fitToContent
              disableGutters={disableGutters}
              disableScrollbar={disableScrollBar}
            >
              {children}
            </Content>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
